'use client';

import React, { useEffect, useState } from 'react';
import { css, cx } from 'styled-system/css';

import Tooltip, { type TooltipPlacement } from '@shaojiang/components/Tooltip';

interface Props {
  text: string;
  tipText?: string;
  children?: React.ReactNode;
  tipPlacement?: TooltipPlacement;
  className?: string;
}

export default function Copiable({
  text,
  tipText = 'Copy',
  children = null,
  tipPlacement = 'top',
  className,
}: Props) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    window.navigator.clipboard.writeText(text).then(
      () => setCopied(true),
      () =>
        window.prompt("Whoops, we can't copy in your browser. Please copy your text here:", text),
    );
  };

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 3000);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <Tooltip title={copied ? 'Copied' : tipText} placement={tipPlacement}>
      <div
        role='button'
        tabIndex={0}
        onClick={copy}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && copy()}
        className={cx(css({ display: 'inline-block', cursor: 'pointer' }), className)}
      >
        {children || text}
      </div>
    </Tooltip>
  );
}
